import { Box, Text } from "theme-ui";
import Icon from "./common/Icon";
import { useState, useEffect } from "react";
import apiInvocation from "../api/apiInvocation";

interface EndangeredStatusToggleProps {
    id: string;
    isEndangered: boolean;
    isInvasive: boolean;
    onStatusChange: (newStatus: boolean) => void;
    isAdmin?: boolean;
    hasEnlistedOnEndangeredSpecies: boolean;
    isOnPBVList: boolean | null;
}

const EndangeredStatusToggle = ({
                                    id,
                                    isAdmin,
                                    isEndangered,
                                    isInvasive,
                                    onStatusChange,
                                    isOnPBVList,
                                    hasEnlistedOnEndangeredSpecies,
                                }: EndangeredStatusToggleProps) => {
    const [localStatus, setLocalStatus] = useState<"endangered" | "invasive" | "none">(
        isEndangered ? "endangered" : isInvasive ? "invasive" : "none"
    );
    const [isUpdating, setIsUpdating] = useState(false);

    // Sync localStatus when props change
    useEffect(() => {
        if (isEndangered) {
            setLocalStatus("endangered");
        } else if (isInvasive) {
            setLocalStatus("invasive");
        } else {
            setLocalStatus("none");
        }
    }, [isEndangered, isInvasive]);

    const handleToggle = async () => {
        if (!isAdmin || isUpdating) return;

        const nextStatus =
            localStatus === "endangered"
                ? "invasive"
                : localStatus === "invasive"
                    ? "none"
                    : "endangered";

        setLocalStatus(nextStatus);
        setIsUpdating(true);

        try {
            await apiInvocation.overrideStatus(id);
            onStatusChange(nextStatus === "endangered");
        } catch (error) {
            console.error("Error updating threat status:", error);
            // Rollback to previous state if failed
            setLocalStatus(localStatus);
        } finally {
            setIsUpdating(false);
        }
    };

    const generateConditionForPBV = () => {
        return isOnPBVList ?? hasEnlistedOnEndangeredSpecies;
    };

    const getStatusProps = () => {
        switch (localStatus) {
            case "endangered":
                return {
                    icon: "warning",
                    text: "Endangered",
                    background: "error",
                };
            case "invasive":
                return {
                    icon: "bug",
                    text: "Invasive",
                    background: "invasiveTagColor",
                };
            case "none":
            default:
                return {
                    icon: "check-circle-regular",
                    text: "Not Endangered",
                    background: "secondaryButton",
                };
        }
    };

    const { icon, text, background } = getStatusProps();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 3,
                height: ["26px", "36px"],
                background,
                borderRadius: 8,
                pr: [5, 7],
                pl: [4, 6],
                mb: [4, 6],
                mt: [4, 6],
                cursor: isAdmin && !isUpdating ? "pointer" : "default",
                opacity: isUpdating ? 0.7 : 1,
            }}
            onClick={isAdmin ? handleToggle : undefined}
        >
            <Icon name={icon} size={[12, 16]} color="buttonText" />
            <Text sx={{ fontSize: [0, 2], color: "buttonText", fontWeight: "medium" }}>
                {text}
            </Text>
            {generateConditionForPBV() && (
                <Icon name="verified_user" size={[12, 16]} color="buttonText" />
            )}
        </Box>
    );
};

export default EndangeredStatusToggle;